import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, CheckCircle2, LifeBuoy, Sparkles, Send } from "lucide-react";
import { TicketThread } from "@/components/TicketReply";
import { VoiceButton } from "@/components/VoiceButton";

const STATUS_LABEL = {
  new: "New",
  in_progress: "In progress",
  waiting_on_customer: "Waiting on you",
  resolved: "Resolved",
  closed: "Closed",
};

export default function TicketStatus() {
  const { token } = useParams();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => api.get(`/tickets/by-token/${token}`).then((r) => setTicket(r.data)).catch(() => setError("Ticket not found")), [token]);
  useEffect(() => { load(); }, [load]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    const fd = new FormData(); fd.append("message", reply.trim());
    try { await api.post(`/tickets/by-token/${token}/reply`, fd); setReply(""); toast.success("Sent to David"); load(); }
    catch { toast.error("Couldn't send — please try again"); }
    finally { setSending(false); }
  };

  if (error) {
    return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
      <div className="dg-card rounded-2xl p-8">
        <h1 className="font-display text-2xl">Ticket not found</h1>
        <p className="text-slate-400 mt-2">Check the tracking link in your email, or open a new ticket.</p>
        <Link to="/ask" className="dg-btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 mt-4"><LifeBuoy className="w-4 h-4" /> Ask Dr.Gadget</Link>
      </div>
    </div>;
  }
  if (!ticket) return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-slate-400">Loading…</div>;

  const isResolved = ticket.status === "resolved" || ticket.status === "closed";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-6">
        <span className="dg-eyebrow">Ticket tracker</span>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">Ticket {ticket.ticket_id}</h1>
        <p className="text-slate-400 mt-2">{ticket.issue_type} · urgency {ticket.urgency}</p>
      </div>

      <div className="dg-card rounded-2xl p-6 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-full grid place-items-center ${isResolved ? "bg-emerald-500/15 border border-emerald-500/40" : "bg-cyan-500/10 border border-cyan-500/40"}`}>
          {isResolved ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> : <Clock className="w-6 h-6 text-cyan-400" />}
        </div>
        <div>
          <div className="dg-eyebrow">Status</div>
          <div className="font-display text-lg font-semibold" data-testid="ticket-status">{STATUS_LABEL[ticket.status] || ticket.status}</div>
          {ticket.status_note && <div className="text-sm text-slate-400 mt-1">"{ticket.status_note}"</div>}
        </div>
      </div>

      {ticket.ai_suggestion && (
        <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-6 mt-6" data-testid="ticket-ai-suggestion">
          <div className="dg-eyebrow mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3" /> First-pass suggestions</div>
          <p className="text-slate-200 whitespace-pre-wrap text-sm leading-relaxed">{ticket.ai_suggestion}</p>
        </div>
      )}

      <div className="dg-card rounded-2xl p-6 mt-6">
        <div className="dg-eyebrow mb-2">Your description</div>
        <p className="text-slate-300 whitespace-pre-wrap text-sm">{ticket.description}</p>
        {ticket.files?.length > 0 && (
          <div className="mt-4">
            <div className="dg-eyebrow mb-2">Attachments</div>
            <ul className="text-sm text-slate-300 space-y-1 font-mono">
              {ticket.files.map((f) => <li key={f.original_filename}>• {f.original_filename}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="dg-card rounded-2xl p-6 mt-6" data-testid="ticket-conversation">
        <div className="dg-eyebrow mb-4">Conversation with David</div>
        <TicketThread messages={ticket.messages} />
        <form onSubmit={sendReply} className="mt-4">
          <div className="dg-eyebrow mb-1 flex items-center justify-between"><span>Reply to David</span><VoiceButton testId="ticket-voice-btn" onText={(t) => setReply((r) => (r ? r + " " : "") + t)} /></div>
          <textarea className="dg-input" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add details, answer a question, or say it's fixed…" data-testid="ticket-reply-input" />
          <button type="submit" disabled={sending || !reply.trim()} className="dg-btn-primary rounded-full px-5 py-2 text-sm mt-2 flex items-center gap-2 disabled:opacity-50" data-testid="ticket-reply-send">
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send reply"}
          </button>
        </form>
      </div>

      <div className="dg-card rounded-2xl p-6 mt-6">
        <div className="dg-eyebrow mb-4">Activity</div>
        {(!ticket.updates || ticket.updates.length === 0) ? (
          <div className="text-sm text-slate-500">No updates yet — David will respond soon. You'll get an email when the status changes.</div>
        ) : (
          <ol className="space-y-4">
            {ticket.updates.slice().reverse().map((u) => (
              <li key={u.at} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 shrink-0" />
                <div>
                  <div className="text-sm text-slate-200">Status → <span className="text-cyan-300 font-medium">{STATUS_LABEL[u.status] || u.status}</span></div>
                  {u.note && <div className="text-sm text-slate-400 mt-1">{u.note}</div>}
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mt-1">{new Date(u.at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link to="/ask" className="dg-btn-ghost rounded-full px-5 py-2 text-sm">Open a new ticket</Link>
      </div>
    </div>
  );
}
